import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Slide, { type SlideProps } from "@mui/material/Slide";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import { shellBottomBarHeight } from "_shared/_views/shell-bottom-bar";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";
import type React from "react";
import type { ReactNode } from "react";

/** What the work agreement's chat pane is called (#5565). A proposal names its own pane (#5818). */
export const correspondenceTitle = "Correspondence";

/** The paper's width where the page does not say otherwise: 400px, or the whole window on a phone. */
const DEFAULT_WIDTH = "min(400px, 100vw)";

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) =>
    prop !== "paperWidth" && prop !== "inReservedColumn" && prop !== "overlay" && prop !== "closeAfterTransition",
})<{ paperWidth: string; inReservedColumn: boolean; overlay: boolean }>(({ theme, paperWidth, inReservedColumn, overlay }) => {
  if (overlay) {
    return {
      // Over the page, so it takes none of the page's row. Fixed to the window like any drawer's
      // paper, and ended above the assistant's bar, which stays reachable while the pane is open.
      flexShrink: 0,
      width: 0,
      "& .MuiDrawer-paper": {
        bottom: shellBottomBarHeight(),
        boxShadow: theme.shadows[16],
        boxSizing: "border-box",
        height: "auto",
        overflowX: "hidden",
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(8),
        // Below the banners, which push the app bar down from the top of the window.
        top: shellTopBannersHeight(),
        width: paperWidth,
      },
    };
  }

  return inReservedColumn
    ? {
        // The page has already set a column of this width aside, so the drawer takes none of its own:
        // a second claim on the row made the page wider than the window. It starts under the page
        // header rather than behind it.
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          height: `calc(100% - ${shellTopBannersHeight()})`,
          marginTop: theme.spacing(8),
          top: shellTopBannersHeight(),
          width: paperWidth,
        },
      }
    : {
        flexShrink: 0,
        width: paperWidth,
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          height: `calc(100% - ${shellTopBannersHeight()})`,
          overflowX: "hidden",
          paddingBottom: theme.spacing(2),
          // The page header sits over the top of the paper, so the pane starts below it. The paper
          // starts below the banners, which push the page header down from the top of the window.
          paddingTop: theme.spacing(8),
          top: shellTopBannersHeight(),
          width: paperWidth,
        },
      };
});
const PanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
// Fills the paper rather than restating its width. The paper narrows to the viewport below 400px and a
// 400px literal did not, so on a phone the pane's right padding, which the timeline scrubber sits in
// front of, went under the paper's edge.
const PanelContent = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    paddingTop: 0,
  },
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  padding: theme.spacing(2),
  width: "100%",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));

function RightAnchoredDrawerSlide(props: Readonly<SlideProps>) {
  return <Slide {...props} direction="left" />;
}

type CorrespondenceDrawerProps = {
  isOpen: boolean;
  /**
   * Paints the pane over the page instead of beside it. The page decides: it knows whether its row has
   * room for the pane. A pane left beside a page with no room for it was drawn under the page's content,
   * which is how a proposal's Correspondence pane could not be seen at all on a phone.
   *
   * Over the page, but not modal. It was a modal drawer, and a modal puts the rest of the app behind
   * it: inert, and hidden from assistive technology. The assistant docks beside the page (#6128) so
   * that a user can read this pane while they ask about it, which a modal ruled out.
   */
  isOverlay?: boolean;
  /** The paper's width. The page passes the width it reserves in its row, so the two agree. */
  width?: string;
  /**
   * The page sets a column of `width` aside for the pane in its own row, as the proposal pages do. The
   * drawer then claims no width of its own. The work agreement page makes room another way.
   */
  inReservedColumn?: boolean;
  title?: string;
  /** What the minimize button says it does. A page that names its pane something else says so here. */
  minimizeLabel?: string;
  className?: string;
  "data-testid"?: string;
  children: ReactNode;
  onClose: () => void;
};

/**
 * The right-hand drawer a Correspondence pane opens in, with its title and its minimize button.
 *
 * One drawer for the work agreement's chat and the proposal's comments. Each used to draw its own, and
 * they drifted: the work agreement's became an overlay on a narrow window, and the proposal's stayed
 * beside the page whatever the width, where the page painted over it.
 */
export default function CorrespondenceDrawer(props: Readonly<CorrespondenceDrawerProps>) {
  const { isOpen, isOverlay, title, minimizeLabel, className, children, onClose } = props;
  const width = props.width ?? DEFAULT_WIDTH;

  // Escape put the pane away while it was modal, and still does.
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== KeyboardKey.Escape || !isOverlay) return;
    event.stopPropagation();
    onClose();
  }

  return (
    <Drawer
      className={className}
      data-testid={props["data-testid"]}
      paperWidth={isOverlay ? DEFAULT_WIDTH : width}
      inReservedColumn={Boolean(props.inReservedColumn) && !isOverlay}
      overlay={Boolean(isOverlay)}
      open={isOpen}
      anchor="right"
      variant="persistent"
      closeAfterTransition={false}
      slots={{ transition: RightAnchoredDrawerSlide }}
      // Tells a page's side-panel column that this paper lies over the page rather than in the
      // column, so the column leaves it where it is. See `ProposalSidePanelColumn`.
      slotProps={{ paper: { "data-overlay": isOverlay ? "true" : undefined } as object }}
      onClose={onClose}
      onKeyDown={handleKeyDown}
    >
      <PanelContainer>
        <PanelContent>
          <TitleBar>
            <Typography variant="h5">{title || correspondenceTitle}</Typography>
            {/* Not interactive: the button can open right under the pointer that clicked the page's
                Comments button, and an interactive label stayed open there over the pane's
                conversation tabs, taking their clicks. */}
            <Tooltip title={minimizeLabel || "Minimize correspondence panel"} disableInteractive>
              <IconButton size="medium" onClick={onClose}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          {children}
        </PanelContent>
      </PanelContainer>
    </Drawer>
  );
}
